import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { map, tap, shareReplay } from 'rxjs/operators';
import { API_BASE_URL } from '../api-base-url.token';

export interface Message {
  id: string;
  establishmentId: string;
  auteur: string;
  titre: string;
  contenu: string;
  type: 'Humain' | 'IA';
  timestamp: string;
  updatedAt?: string;
  commentaires?: Commentaire[];
  canDelete?: boolean;
  canEdit?: boolean;
}

export interface Commentaire {
  id: string;
  auteur: string;
  contenu: string;
  timestamp: string;
  canDelete?: boolean;
  canEdit?: boolean;
}

export interface PagedResponse<T> {
  content: T[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  hasNext?: boolean;
}

export interface CreateMessagePayload {
  titre: string;
  contenu: string;
  mentioned_user_ids?: string[];
  mention_all?: boolean;
}

export interface CreateCommentairePayload {
  contenu: string;
  mentioned_user_ids?: string[];
  mention_all?: boolean;
}

export interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  last_login_at?: string;
  permissions?: string[];
  job_title_ids?: string[];
  active_skill_ids?: string[];
  establishment_ids?: string[];
  avatar_color?: string;
  locale?: string;
}

export interface UsersPagedResponse {
  users: User[];
  total: number;
  page: number;
  size: number;
}

@Injectable({
  providedIn: 'root'
})
export class ActualiteService {
  private http = inject(HttpClient);
  private base = inject(API_BASE_URL);

  getMessages(establishmentId: string, page = 0, size = 50): Observable<PagedResponse<Message>> {
    const params = {
      page: page.toString(),
      size: size.toString(),
      sort: 'timestamp,desc'
    };

    return this.http.get<PagedResponse<Message>>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/messages`,
      { params }
    );
  }

  createMessage(establishmentId: string, payload: CreateMessagePayload): Observable<Message> {
    return this.http.post<Message>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/messages`,
      payload
    );
  }

  addCommentaire(establishmentId: string, messageId: string, payload: CreateCommentairePayload): Observable<Commentaire> {
    return this.http.post<Commentaire>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/messages/${messageId}/commentaires`,
      payload
    );
  }

  private usersCache = new Map<string, {id: string, name: string, email: string}[]>();

  getUsers(establishmentId: string): Observable<{id: string, name: string, email: string}[]> {
    if (this.usersCache.has(establishmentId)) {
      return of(this.usersCache.get(establishmentId)!);
    }
    
    return this.http.get<{id: string, name: string, email: string}[]>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/users`
    ).pipe(
      tap(users => this.usersCache.set(establishmentId, users))
    );
  }

  /** Mutualise les appels identiques (gestion-equipes, site, user-list-modal demandent souvent la même page).
   *  Invalidé entièrement sur toute création/modification/suppression d'utilisateur. */
  private usersPagedCache = new Map<string, Observable<UsersPagedResponse>>();

  getUsersPaginated(page = 0, size = 20): Observable<UsersPagedResponse> {
    const key = `${page}:${size}`;
    if (!this.usersPagedCache.has(key)) {
      const params = { page: page.toString(), size: size.toString() };
      this.usersPagedCache.set(
        key,
        this.http.get<UsersPagedResponse>(`${this.base}/api/v1/users`, { params }).pipe(shareReplay(1))
      );
    }
    return this.usersPagedCache.get(key)!;
  }

  /** À appeler avant de redemander la liste après une mutation qui ne passe pas par
   *  createUser/updateUser/deleteUser (ex: TeamService.setUserJobTitles/Skills/Establishments). */
  invalidateUsersPagedCache(): void {
    this.usersPagedCache.clear();
  }

  /** À appeler au logout : ces caches ne sont pas scopés par organisation, donc un login
   *  vers une autre org sans rechargement de page servirait sinon les données de l'org précédente. */
  clearAllCaches(): void {
    this.usersCache.clear();
    this.usersPagedCache.clear();
  }

  createUser(payload: { first_name: string; last_name: string; email: string; permissions: string[] }): Observable<{ user_id: string; title: string; message: string }> {
    return this.http.post<{ user_id: string; title: string; message: string }>(`${this.base}/api/v1/users`, payload)
      .pipe(tap(() => this.usersPagedCache.clear()));
  }

  updateUser(userId: string, payload: { first_name: string; last_name: string; permissions: string[]; locale?: string }): Observable<{ title: string; message: string }> {
    return this.http.put<{ title: string; message: string }>(`${this.base}/api/v1/users/${userId}`, payload)
      .pipe(tap(() => this.usersPagedCache.clear()));
  }

  deleteUser(userId: string): Observable<{ title: string; message: string }> {
    return this.http.delete<{ title: string; message: string }>(`${this.base}/api/v1/users/${userId}`)
      .pipe(tap(() => this.usersPagedCache.clear()));
  }

  getUnreadMentionsCount(establishmentId: string): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/mentions/unread-count`
    );
  }

  markMentionsAsRead(establishmentId: string): Observable<void> {
    return this.http.post<void>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/mentions/mark-read`,
      {}
    );
  }

  deleteMessage(establishmentId: string, messageId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/messages/${messageId}`
    );
  }

  deleteCommentaire(establishmentId: string, commentaireId: string): Observable<void> {
    return this.http.delete<void>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/commentaires/${commentaireId}`
    );
  }

  updateMessage(establishmentId: string, messageId: string, payload: { titre: string; contenu: string }): Observable<Message> {
    return this.http.put<Message>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/messages/${messageId}`,
      payload
    );
  }

  updateCommentaire(establishmentId: string, commentaireId: string, payload: { contenu: string }): Observable<Commentaire> {
    return this.http.put<Commentaire>(
      `${this.base}/api/v1/establishments/${establishmentId}/actualite/commentaires/${commentaireId}`,
      payload
    );
  }
}
